import { ArrowBackIcon, ArrowForwardIcon, CloseIcon, HamburgerIcon } from '@chakra-ui/icons';
import { keyframes } from '@emotion/react';
import {
  Grid,
  Flex,
  Hide,
  GridItem,
  Icon,
  chakra,
  Box,
  Link,
  Show,
  Modal,
  useDisclosure,
  Button,
  IconButton,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  ModalHeader
} from '@chakra-ui/react';
import { motion, useScroll } from 'framer-motion';
import * as React from 'react';
import { BsInstagram, BsTiktok, BsFacebook, BsYoutube, BsTranslate } from 'react-icons/bs';
import LogoSingle from '../assets/images/logo_single.svg';
import LogoText from '../assets/images/logo_text.svg';
import LogoSingleBlack from '../assets/images/logo_single_black.svg';
import { useTranslationsContext } from './useTranslations';
import LocalizedLink from './localizedLink';
import { BiFullscreen } from 'react-icons/bi';
import { CarImage } from './carImages';
import { ImagesPreview } from './imagesPreview';
import { FadeUp } from './fadeUp';
import { FadeDown } from './fadeDown';
import { LocaleContext } from './layout';
import { SelectLanguageModal } from './selectLanguageModal';

type Props = {
  style: 'light' | 'dark';
  id?: string;
};

const animationKeyframes = keyframes`
  0% { left: 0; width: 0; }
  50% { left: 0; width: 100%; }
  100% { left: 100%; width: 0; }
`;

const animation = `${animationKeyframes} .6s ease-in-out`;

const ChakraLogoSingle = chakra(LogoSingle);
const ChakraLogoSingleBlack = chakra(LogoSingleBlack);
const ChakraLogoText = chakra(LogoText);

export const NavBar: React.FC<Props> = ({ style, id }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: selectLanguageOpen, onOpen: onSelectLanguageOpen, onClose: onSelectLanguageClose } = useDisclosure();
  const { scrollYProgress } = useScroll();
  const [isExpanded, setIsExpanded] = React.useState(true);
  const { navigation_about_us, navigation_cars, navigation_services, navigation_contact, navigation_news, alt_logo_single, alt_logo_text, home } =
    useTranslationsContext();

  const { locale, catalogLocales } = React.useContext(LocaleContext);

  React.useEffect(() => {
    scrollYProgress.on('change', v => {
      setIsExpanded(v <= 0);
    });
    return () => {
      scrollYProgress.clearListeners();
    };
  }, [scrollYProgress]);

  const links = (
    <Flex align="center" gap={[4, null, null, null, 12]} direction={['column', null, null, null, 'row']}>
      <Hide above="lg">
        <LocalizedLink
          to="/"
          fontSize={['4xl', null, null, null, 'lg']}
          fontWeight="bold"
          _hover={{
            color: 'brand'
          }}
        >
          {home}
        </LocalizedLink>
      </Hide>
      <LocalizedLink
        fontSize={['4xl', null, null, null, 'lg']}
        fontWeight="bold"
        _hover={{
          color: 'brand'
        }}
        to="/about-us"
      >
        {navigation_about_us}
      </LocalizedLink>
      <LocalizedLink
        fontSize={['4xl', null, null, null, 'lg']}
        fontWeight="bold"
        _hover={{
          color: 'brand'
        }}
        to="/cars"
      >
        {navigation_cars}
      </LocalizedLink>
      <LocalizedLink
        fontSize={['4xl', null, null, null, 'lg']}
        fontWeight="bold"
        _hover={{
          color: 'brand'
        }}
        to="/services"
      >
        {navigation_services}
      </LocalizedLink>
      <LocalizedLink
        fontSize={['4xl', null, null, null, 'lg']}
        fontWeight="bold"
        _hover={{
          color: 'brand'
        }}
        to="/news"
      >
        {navigation_news}
      </LocalizedLink>
      <LocalizedLink
        fontSize={['4xl', null, null, null, 'lg']}
        fontWeight="bold"
        _hover={{
          color: 'brand'
        }}
        to="/contact"
      >
        {navigation_contact}
      </LocalizedLink>
    </Flex>
  );

  const social = (
    <Flex align="center" justifySelf={'end'}>
      <IconButton
        icon={<BsInstagram />}
        aria-label={'Instagram'}
        variant={'link'}
        fontSize={'2xl'}
        mr={2}
        color={'inherit'}
        _hover={{
          color: 'brand'
        }}
        onClick={() => {
          window.open('https://www.instagram.com/autocentermeschede/','_blank');
        }}
      />
      <IconButton
        icon={<BsTiktok />}
        aria-label={'TikTok'}
        variant={'link'}
        fontSize={'2xl'}
        color={'inherit'}
        mr={2}
        _hover={{
          color: 'brand'
        }}
        onClick={() => {
          window.open('https://www.tiktok.com/@autocentermeschede','_blank');
        }}
      />
      <IconButton
        icon={<BsFacebook />}
        aria-label={'Facebook'}
        variant={'link'}
        fontSize={'2xl'}
        color={'inherit'}
        mr={2}
        _hover={{
          color: 'brand'
        }}
        onClick={() => {
          window.open('https://www.facebook.com/autocentermeschede','_blank');
        }}
      />
      <IconButton
        icon={<BsYoutube />}
        aria-label={'YouTube'}
        variant={'link'}
        fontSize={'2xl'}
        color={'inherit'}
        _hover={{
          color: 'brand'
        }}
        onClick={() => {
          window.open('https://www.youtube.com/channel/UCIl78OPcJf6aIxIB87v8-VQ','_blank');
        }}
      />
      <Button
        fontWeight={'medium'}
        color={'inherit'}
        leftIcon={<Icon as={BsTranslate} />}
        variant="link"
        ml={4}
        _hover={{
          color: 'brand'
        }}
        onClick={onSelectLanguageOpen}
      >
        {locale == 'de-DE' ? 'DE' : locale == 'en-US' ? 'EN' : 'FR'}
      </Button>
    </Flex>
  );

  return (
    <Grid
      w="100%"
      pos="fixed"
      zIndex={1000}
      px={!isExpanded ? [4, null, null, null, 7] : [7, null, null, null, 10]}
      height={!isExpanded ? '75px' : '120px'}
      as={motion.div}
      transition="all 0.3s ease-out"
      bg={!isExpanded ? (style == 'light' ? 'white' : 'black') : 'transparent'}
      templateColumns={['1fr 1fr', '1fr 1fr', '1fr 1fr', null, '1fr 4fr 1fr']}
      color={style == 'light' ? 'black' : 'white'}
      alignItems="center"
      boxShadow={!isExpanded ? 'base' : undefined}
      id={id}
    >
      <LocalizedLink to="/" aria-label={alt_logo_text}>
        <Flex align="center" gap="3">
          {style == 'light' ? (
            <ChakraLogoSingleBlack height={['26px', null, null, null, '34px']} width="auto" alt={alt_logo_single} />
          ) : (
            <ChakraLogoSingle height={['26px', null, null, null, '34px']} width="auto" alt={alt_logo_single} />
          )}
          <Box pos="relative">
            <ChakraLogoText
              height={['26px', null, null, null, '34px']}
              width="auto"
              alt={alt_logo_text}
              sx={{
                fill: style == 'light' ? 'black' : '#fff'
              }}
            />
          </Box>
        </Flex>
      </LocalizedLink>
      <Show above="xl">
        <GridItem colSpan={1} justifySelf="center">
          {links}
        </GridItem>
        {social}
      </Show>
      <Hide above="xl">
        <Flex align="center" justify="end">
          <IconButton
            icon={<HamburgerIcon />}
            boxSize={6}
            aria-label={'Open menu'}
            onClick={onOpen}
            variant={'unstyled'}
          />
        </Flex>

        <Modal isOpen={isOpen} onClose={onClose} size={'full'} motionPreset={'none'}>
          <ModalOverlay />
          <ModalContent position={'relative'} bgColor={'white'}>
            <ModalHeader>
              <Flex align={'center'} justify={'center'} mt={10}>
                <Flex align="center" gap="3">
                  <ChakraLogoSingleBlack height={['26px', null, null, null, '34px']} width="auto" alt={alt_logo_single} />
                  <Box pos="relative">
                    <ChakraLogoText
                      height={['26px', null, null, null, '34px']}
                      width="auto"
                      alt={alt_logo_text}
                      sx={{
                        fill: 'black'
                      }}
                    />
                  </Box>
                </Flex>
              </Flex>
            </ModalHeader>
            <ModalCloseButton zIndex={10} />
            <ModalBody
              as={Flex}
              align={'center'}
              h={'full'}
              direction="column"
              color={'brand'}
              pb={'env(safe-area-inset-bottom)'}
              mt={6}
            >
              <FadeDown>{links}</FadeDown>
              <FadeDown mt={12} color={'black'}>
                {social}
              </FadeDown>
            </ModalBody>
          </ModalContent>
        </Modal>
      </Hide>

      <SelectLanguageModal isOpen={selectLanguageOpen} onClose={onSelectLanguageClose} locale={locale} catalogLocales={catalogLocales} />
    </Grid>
  );
};
